import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Card } from "./ui/card";
import {
  Key,
  Mail,
  Loader2,
  CheckCircle,
  AlertCircle,
  Copy,
  Send
} from "lucide-react";
import { motion } from "motion/react";
import { toast } from "sonner";
import accessCodeService, { AccessCode } from "../services/accessCode.service";

interface AdminAccessCodeGeneratorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function AdminAccessCodeGenerator({
  open,
  onOpenChange,
  onSuccess
}: AdminAccessCodeGeneratorProps) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generatedCode, setGeneratedCode] = useState<AccessCode | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const response = await accessCodeService.generateAccessCode({
        email: email.trim().toLowerCase(),
        paymentType: "full",
      });

      setGeneratedCode(response.data.accessCode);
      toast.success("Access code generated and sent to user!");
      onSuccess?.();
    } catch (err: any) {
      setError(err.message || "Failed to generate access code. Please try again.");
      toast.error(err.message || "Failed to generate access code");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEmail("");
    setError(null);
    setGeneratedCode(null);
    onOpenChange(false);
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard!");
    } catch {
      toast.error("Failed to copy");
    }
  };

  const handleGenerateAnother = () => {
    setEmail("");
    setError(null);
    setGeneratedCode(null);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Key className="w-5 h-5 text-[#d4af37]" />
            Generate Access Code
          </DialogTitle>
          <DialogDescription>
            Generate a booking access code for a user who has made their payment.
          </DialogDescription>
        </DialogHeader>

        {generatedCode ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="py-4 space-y-6"
          >
            {/* Success Message */}
            <div className="text-center space-y-2">
              <div className="mx-auto w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-green-600">Code Generated!</h3>
              <p className="text-sm text-gray-600">
                An email has been sent to the user with their access code.
              </p>
            </div>

            {/* Generated Code Display */}
            <Card className="p-4 bg-gradient-to-br from-[#d4af37]/10 to-[#c9a429]/10 border-[#d4af37]">
              <div className="text-center space-y-3">
                <p className="text-sm text-gray-600">Access Code</p>
                <div className="flex items-center justify-center gap-2">
                  <code className="text-3xl font-bold tracking-widest text-[#0f1f3d] bg-white px-4 py-2 rounded">
                    {generatedCode.code}
                  </code>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(generatedCode.code)}
                    className="h-10 w-10 p-0"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Card>

            {/* Code Details */}
            <Card className="p-4 bg-gray-50">
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">User Email:</span>
                  <span className="font-medium">{generatedCode.userId?.email || email}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Expires:</span>
                  <span>{new Date(generatedCode.expiresAt).toLocaleString()}</span>
                </div>
              </div>
            </Card>

            {/* Info */}
            <div className="flex gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <Send className="w-5 h-5 text-blue-600 flex-shrink-0" />
              <p className="text-sm text-blue-900">
                The user will receive an email with the access code and instructions
                to redeem it on the booking page.
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={handleClose}
                className="flex-1"
              >
                Close
              </Button>
              <Button
                onClick={handleGenerateAnother}
                className="flex-1 btn-gold"
              >
                Generate Another
              </Button>
            </div>
          </motion.div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6 py-4">
            {/* Email Input */}
            <div className="space-y-2">
              <Label htmlFor="user-email" className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                User Email
              </Label>
              <Input
                id="user-email"
                type="email"
                placeholder="student@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                className="h-11"
              />
              <p className="text-xs text-gray-500">
                The user must have a verified account with this email.
              </p>
            </div>

            {/* Info Card */}
            <Card className="p-4 bg-blue-50 border-blue-200">
              <div className="flex gap-3">
                <Key className="w-5 h-5 text-blue-600 flex-shrink-0" />
                <div className="text-sm text-blue-900">
                  <p className="font-medium mb-1">What happens next?</p>
                  <ul className="text-xs space-y-1 text-blue-800">
                    <li>A 6-character access code will be generated</li>
                    <li>The code will be emailed to the user</li>
                    <li>The code expires in 24 hours</li>
                    <li>Any previous codes for this user will be invalidated</li>
                  </ul>
                </div>
              </div>
            </Card>

            {/* Error Message */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm"
              >
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{error}</span>
              </motion.div>
            )}

            {/* Submit Button */}
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                className="flex-1"
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 btn-gold"
                disabled={loading || !email}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Key className="w-4 h-4 mr-2" />
                    Generate & Send Code
                  </>
                )}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default AdminAccessCodeGenerator;
